import { useState } from "react";
import { usePagedOrdersReadyForSowing } from "../hooks/useOrders";
import type { Order } from "../services/ordersApi";

import type { SowingInstruction } from "../services/sowingInstructionsApi";
import { ApproveSowingDialog } from "../components/sowingInstructions/ApproveSowingDialog";
import { RecordSowingDialog } from "../components/sowingInstructions/RecordSowingDialog";
import { RecordGerminationWithPreviewDialog, SelectGerminationBatchDialog } from "../components/sowingInstructions/PhaseWorkflowDialogs";
import { RecordGrowthDialog } from "../components/sowingInstructions/RecordGrowthDialog";
import { RecordDispatchDialog } from "../components/sowingInstructions/RecordDispatchDialog";
import { RevokeSowingDialog } from "../components/sowingInstructions/RevokeSowingDialog";
import { Button } from "@/components/ui/button";
import { SowingInstructionsTable } from "../components/sowingInstructions/SowingInstructionsTable";
import { StockEntryWorkflowTable } from "../components/stockEntryPipeline/StockEntryWorkflowTable";
import { OrdersReadyForSowingTable } from "../components/sowingInstructions/OrdersReadyForSowingTable";
import { CreateSowingInstructionDialog } from "../components/sowingInstructions/CreateSowingInstructionDialog";
import { BulkImportStockDialog } from "../components/sowingInstructions/BulkImportStockDialog";
import { useAuthStore } from "@/features/auth/stores/authStore";

const SOWING_INSTRUCTION_STATUSES = [
    { value: 0, label: "Pending" },
    { value: 1, label: "Issued" },
    { value: 2, label: "Sown" },
    { value: 3, label: "Germinated" },
    { value: 4, label: "Growing" },
    { value: 5, label: "Ready for Dispatch" },
    { value: 6, label: "Stored" },
    { value: 7, label: "Dispatched" },
    { value: 8, label: "Overgrown" },
    { value: 9, label: "Revoked" },
    { value: 10, label: "On Hold" },
    { value: 11, label: "Ready for Growing" },
    { value: 12, label: "Draft" },
    { value: 13, label: "Partially Sown" },
];

type FilterType = 0 | 1 | 2 | 3 | 4 | 5 | 6;

const FILTER_OPTIONS: { value: FilterType; label: string }[] = [
    { value: 5, label: "Orders Ready for Sowing" },
    { value: 0, label: "Pending Approval" },
    { value: 1, label: "Sowing" },
    { value: 2, label: "Germination" },
    { value: 3, label: "Growth" },
    { value: 4, label: "Record Ready for Dispatch" },
    { value: 6, label: "Revoked" },
];

export const SowingManagementPage = () => {
    const { hasPermission } = useAuthStore();
    const canCreate = hasPermission("sowing_instructions.create");
    const canApprove = hasPermission("sowing_instructions.approve");
    const canRecordSowing = hasPermission("sowing_instructions.record_sowing");
    const canRecordGermination = hasPermission("sowing_instructions.record_germination");
    const canRevoke = hasPermission("sowing_instructions.revoke");
    const [filterType, setFilterType] = useState<FilterType>(5);
    const [ordersPageNumber, setOrdersPageNumber] = useState(1);
    const [ordersPageSize, setOrdersPageSize] = useState(20);
    const [ordersSearch, setOrdersSearch] = useState("");
    const [selectedInstruction, setSelectedInstruction] =
        useState<SowingInstruction | null>(null);
    const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
    const [selectedOrderItemId, setSelectedOrderItemId] = useState<string>("");
    const [dialogOpen, setDialogOpen] = useState(false);
    const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
    const [sowingDialogOpen, setSowingDialogOpen] = useState(false);

    // Map each management tab to the same status sets the dedicated pages use.
    // Growth tab (3) fetches ReadyForGrowing (11) — instructions land there after
    // germination is acknowledged. Sowing (1) and Germination (2) tabs also include
    // PartiallySown (13) so partial-progress rows are visible.
    const STATUS_MAP: Record<number, number | number[]> = {
        0: 0,         // Pending
        1: [1, 13],   // Issued + PartiallySown
        2: [2, 13],   // Sown + PartiallySown
        3: 11,        // ReadyForGrowing
        4: 4,         // Growing
        6: 9,         // Revoked
    };
    const activeStatuses = filterType !== 5 ? STATUS_MAP[filterType] ?? filterType : 0;
    const ordersReadyQuery = usePagedOrdersReadyForSowing({
        pageNumber: ordersPageNumber,
        pageSize: ordersPageSize,
        search: ordersSearch.trim() || undefined,
    });

    const handleUpdateStatus = (instruction: SowingInstruction) => {
        setSelectedInstruction(instruction);
        setDialogOpen(true);
    };

    const handleRevoke = (instruction: SowingInstruction) => {
        setSelectedInstruction(instruction);
        setRevokeDialogOpen(true);
    };

    const handleDialogSuccess = () => {
        setDialogOpen(false);
        setRevokeDialogOpen(false);
        setSelectedInstruction(null);
        // The mutation invalidates the ["sowingInstructions"] cache prefix, so
        // the table's paged query refetches automatically.
    };

    const canPerformTabAction = (): boolean => {
        switch (filterType) {
            case 0: return canApprove;           // Approve
            case 1: return canRecordSowing;      // Record Sowing
            case 2: return canRecordGermination;  // Record Germination
            case 3: return canRecordGermination;  // Record Growth (same backend permission)
            case 4: return canRecordGermination;  // Record Ready for Dispatch (same backend permission)
            case 6: return false;                 // Revoked — read-only
            default: return false;
        }
    };

    const renderDialog = () => {
        if (!selectedInstruction) return null;

        switch (filterType) {
            case 0: // Pending
                return (
                    <ApproveSowingDialog
                        instruction={selectedInstruction}
                        open={dialogOpen}
                        onOpenChange={setDialogOpen}
                        onSuccess={handleDialogSuccess}
                    />
                );
            case 1: // Issued
                return (
                    <RecordSowingDialog
                        instruction={selectedInstruction}
                        open={dialogOpen}
                        onOpenChange={setDialogOpen}
                        onSuccess={handleDialogSuccess}
                    />
                );
            case 2: { // Sown / Partially Sown
                // Tray-mode instructions are sown in batches; germination must be recorded
                // per batch (wave mode). For one-shot (non-tray) instructions a single
                // SI-level germination event is correct (legacy mode).
                const UNIT_TRAYS = 11;
                const isPartialFlowCapable =
                    selectedInstruction.unit === UNIT_TRAYS ||
                    selectedInstruction.status === 13;
                return isPartialFlowCapable ? (
                    <SelectGerminationBatchDialog
                        instruction={selectedInstruction}
                        open={dialogOpen}
                        onOpenChange={setDialogOpen}
                        onSuccess={handleDialogSuccess}
                    />
                ) : (
                    <RecordGerminationWithPreviewDialog
                        instruction={selectedInstruction}
                        open={dialogOpen}
                        onOpenChange={setDialogOpen}
                        onSuccess={handleDialogSuccess}
                    />
                );
            }
            case 3: // Germinated
                return (
                    <RecordGrowthDialog
                        instruction={selectedInstruction}
                        open={dialogOpen}
                        onOpenChange={setDialogOpen}
                        onSuccess={handleDialogSuccess}
                    />
                );
            case 4: // Growing
                return (
                    <RecordDispatchDialog
                        instruction={selectedInstruction}
                        open={dialogOpen}
                        onOpenChange={setDialogOpen}
                        onSuccess={handleDialogSuccess}
                    />
                );
            default:
                return null;
        }
    };

    const getUpdateButtonLabel = (): string => {
        switch (filterType) {
            case 0: return "Approve";
            case 1: return "Record Sowing";
            case 2: return "Record Germination";
            case 3: return "Record Growth";
            case 4: return "Record Ready for Dispatch";
            default: return "Update";
        }
    };

    // Orders-Ready tab still uses a separate one-shot query; the management
    // tabs delegate loading/empty state to SowingInstructionsTable itself.
    if (filterType === 5 && ordersReadyQuery.isLoading) {
        return (
            <div className="flex items-center justify-center h-96">
                <p>Loading data...</p>
            </div>
        );
    }

    if (filterType === 5 && ordersReadyQuery.error) {
        return (
            <div className="rounded-lg border border-red-200 bg-red-50 p-4">
                <p className="text-red-600">Failed to load data</p>
            </div>
        );
    }

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-3xl font-bold tracking-tight">
                        Sowing Management
                    </h1>
                    <p className="text-gray-500 mt-1">
                        Manage sowing instructions across all stages
                    </p>
                </div>
                <div className="flex gap-2">
                    {hasPermission("sowing_instructions.import") && <BulkImportStockDialog />}
                    {filterType !== 5 && canCreate && <CreateSowingInstructionDialog />}
                </div>
            </div>

            {/* Filter Buttons */}
            <div className="flex gap-2 flex-wrap">
                {FILTER_OPTIONS.map((option) => (
                    <Button
                        key={option.value}
                        variant={filterType === option.value ? "default" : "outline"}
                        onClick={() => {
                            setFilterType(option.value);
                            setOrdersPageNumber(1);
                        }}
                    >
                        {option.label}
                    </Button>
                ))}
            </div>

            {/* Tables - show based on filter type */}
            {filterType === 5 ? (
                <>
                    <OrdersReadyForSowingTable
                        key={`${ordersPageNumber}-${ordersPageSize}-${ordersSearch}`}
                        orders={ordersReadyQuery.data?.items || []}
                        onSelectOrderItem={canCreate ? (order, itemId) => {
                            setSelectedOrder(order);
                            setSelectedOrderItemId(itemId);
                            setSowingDialogOpen(true);
                        } : undefined}
                        isLoading={ordersReadyQuery.isLoading}
                        isFetching={ordersReadyQuery.isFetching}
                        searchTerm={ordersSearch}
                        onSearchTermChange={(value) => {
                            setOrdersSearch(value);
                            setOrdersPageNumber(1);
                        }}
                        currentPage={ordersReadyQuery.data?.currentPage ?? ordersPageNumber}
                        pageSize={ordersReadyQuery.data?.pageSize ?? ordersPageSize}
                        totalCount={ordersReadyQuery.data?.totalCount ?? 0}
                        totalPages={ordersReadyQuery.data?.totalPages ?? 1}
                        onPageChange={setOrdersPageNumber}
                        onPageSizeChange={(size) => {
                            setOrdersPageSize(size);
                            setOrdersPageNumber(1);
                        }}
                    />
                    {/* Sowing Instruction Dialog for Orders */}
                    {selectedOrder && (
                        <CreateSowingInstructionDialog
                            open={sowingDialogOpen}
                            onOpenChange={setSowingDialogOpen}
                            initialCustomerId={selectedOrder.customerId}
                            initialOrderId={selectedOrder.id}
                            initialOrderItemId={selectedOrderItemId}
                        />
                    )}
                </>
            ) : filterType === 3 ? (
                // Growth tab — shows ALL per-wave stock entries in the growth phase:
                // both unacknowledged (needs greenhouse) and growing (in greenhouse, not ready).
                // Row action is contextual: Acknowledge or Mark Ready.
                <StockEntryWorkflowTable
                    stage="growth"
                    actionAllowed={canRecordGermination}
                />
            ) : filterType === 4 ? (
                // Record Ready for Dispatch tab — narrower view: only entries currently growing.
                <StockEntryWorkflowTable
                    stage="mark-ready"
                    actionAllowed={canRecordGermination}
                />
            ) : (
                <>
                    <SowingInstructionsTable
                        serverPagedStatuses={activeStatuses}
                        serverPagedIncludeUnfinishedSowing={filterType === 1}
                        serverPagedIncludeUngerminatedSowings={filterType === 2}
                        statuses={SOWING_INSTRUCTION_STATUSES}
                        onUpdateStatus={canPerformTabAction() ? handleUpdateStatus : undefined}
                        onRevoke={canRevoke ? handleRevoke : undefined}
                        updateStatusLabel={getUpdateButtonLabel()}
                    />

                    {/* Dynamic Dialog */}
                    {renderDialog()}

                    {/* Revoke Dialog */}
                    {selectedInstruction && (
                        <RevokeSowingDialog
                            instruction={selectedInstruction}
                            open={revokeDialogOpen}
                            onOpenChange={setRevokeDialogOpen}
                            onSuccess={handleDialogSuccess}
                        />
                    )}
                </>
            )}
        </div>
    );
};
